import moment from 'moment/moment'

import { IconNameAndCategoryString } from '../components/Icon'
import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { FKType } from './SQLBuilder'
import { Type } from './Type'
import { User } from './User'

type PSLinkConstructorArgs = ConstructorArgs<() => PSLink>

export class PSLink extends BaseRecord {
    readonly recordType = 'pslink'

    label?: string
    url?: string
    type?: string
    seqnum?: number // Integer
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    static SUITELINK_LIMIT = 50

    constructor(data?: Partial<PSLinkConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.url = data.url ?? undefined
        this.type = data.type ?? undefined
        this.label = data.label ?? undefined
        this.seqnum = this.propertyFromIntArg(data.seqnum)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
    }

    clone(overrides?: Partial<PSLinkConstructorArgs>): PSLink {
        return new PSLink({ ...this.serializeForClone(), ...overrides })
    }

    get typeString(): string {
        if (this.type === 'book') return 'Booking widget'
        if (this.type === 'chat') return 'Chat widget'
        if (this.type === 'lead') return 'Lead form'
        if (this.type === 'cash') return 'Payment widget'
        if (this.type === 'shop') return 'Place an order'
        return 'Custom link'
    }

    get icon(): IconNameAndCategoryString {
        if (this.type === 'book') return 'calendar.small'
        if (this.type === 'chat') return 'message.small'
        if (this.type === 'lead') return 'field.small'
        if (this.type === 'cash') return 'dollar.small'
        if (this.type === 'shop') return 'package.small'
        return 'link.small'
    }

    get isDefaultSuiteLink(): boolean {
        return this.type !== 'custom'
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.record) this.record = BaseRecord.fromSerializer(data.record, Type)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
        types: ['record'],
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['label'] = this.label
        dict['type'] = this.type
        dict['url'] = this.url ?? ''
        dict['record'] = this.record?.id ?? ''
        return dict
    }

    static dataType(): DataType {
        return 'links'
    }

    get record(): Type | undefined {
        return this.getForeignRecord('record', Type)
    }
    set record(record: Type | undefined) {
        this.setForeignRecord('record', record)
    }
    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
